import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, Sparkles, Crown, Zap, Loader2, CreditCard, ExternalLink, Info, Plus, RefreshCw, Gift } from "lucide-react";

const PLAN_ICONS = { free: Sparkles, plus: Zap, pro: Crown };
const PLAN_ACCENTS = {
  free: "#6a7570",
  plus: "#4f7942",
  pro: "#c98a3b",
};

const PACK_ACCENTS = {
  credits_5: "#4f7942",
  credits_10: "#c98a3b",
  credits_20: "#8f5b2a",
};

const STATUS_COPY = {
  active: { label: "Active", tone: "#4f7942" },
  trialing: { label: "Trialing", tone: "#4f7942" },
  past_due: { label: "Payment past due", tone: "#c98a3b" },
  unpaid: { label: "Unpaid", tone: "#c95e5e" },
  canceled: { label: "Canceled", tone: "#6a7570" },
  incomplete: { label: "Payment incomplete", tone: "#c95e5e" },
  incomplete_expired: { label: "Payment expired", tone: "#c95e5e" },
};

export default function Plans() {
  const [plans, setPlans] = useState([]);
  const [creditRule, setCreditRule] = useState("0.5 Open Topic Credit = 1 complete AI learning topic");
  const [billing, setBilling] = useState(null);
  const [checkingOut, setCheckingOut] = useState(null);
  const [loadingPortal, setLoadingPortal] = useState(false);
  const [downgrading, setDowngrading] = useState(false);
  const [creditPacks, setCreditPacks] = useState([]);
  const [creditPackNote, setCreditPackNote] = useState("");
  const [buyingPack, setBuyingPack] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const refreshBilling = () => api.get("/billing/me").then((r) => setBilling(r.data)).catch(() => {});

  useEffect(() => {
    // Scroll to the credit-packs section if URL hash requests it
    if (location.hash === "#credit-packs") {
      setTimeout(() => {
        document.getElementById("credit-packs")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 250);
    }
  }, [location.hash]);

  useEffect(() => {
    api.get("/plans").then((r) => {
      setPlans(r.data.plans || []);
      if (r.data.credit_rule) setCreditRule(r.data.credit_rule);
    });
    api.get("/billing/credit-packs").then((r) => {
      setCreditPacks(r.data.packs || []);
      if (r.data.note) setCreditPackNote(r.data.note);
    }).catch(() => {});
    refreshBilling();
    const params = new URLSearchParams(location.search);
    if (params.get("success") === "1") {
      const isPack = params.get("pack") === "1";
      const sessionId = params.get("session_id") || null;
      toast.success(isPack ? "Payment received — adding your credits…" : "Payment received — activating your plan…");
      navigate("/app/plans", { replace: true });
      // Fire-and-forget: tell the backend to reconcile with Stripe immediately.
      // Works even when the webhook can't reach our environment.
      api.post("/billing/sync-checkout", sessionId ? { session_id: sessionId } : {})
        .then((r) => { if (r?.data?.billing) setBilling(r.data.billing); })
        .catch(() => { /* poll below is the fallback */ });
      // Poll for up to 15s in case sync + webhook are both racing.
      let tries = 0;
      const beforeCredits = billing?.credits_remaining ?? null;
      const beforePlan = billing?.plan ?? null;
      const poll = setInterval(async () => {
        tries += 1;
        try {
          const r = await api.get("/billing/me");
          setBilling(r.data);
          window.dispatchEvent(new Event("pm-billing-refresh"));
          const gotPlan = beforePlan === "free" && r.data.plan !== "free";
          const gotCredits = beforeCredits !== null && r.data.credits_remaining > beforeCredits;
          if (gotPlan || (isPack && gotCredits) || tries >= 15) {
            clearInterval(poll);
            if (isPack && gotCredits) {
              toast.success(`Credits added — ${r.data.credits_remaining} total available.`);
            } else if (gotPlan) {
              toast.success(`Welcome to ${r.data.plan_name} — ${r.data.credits_total} credits ready.`);
              if (r.data.credits_topup > 0) {
                setTimeout(() => {
                  toast.success(`🎁 First-subscription bonus unlocked: +${r.data.credits_topup} permanent credits added.`);
                }, 400);
              }
            }
          }
        } catch { /* retry on next tick */ }
      }, 1000);
    } else if (params.get("cancel") === "1") {
      toast.info("Checkout cancelled — you can try again anytime.");
      navigate("/app/plans", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const checkout = async (planId) => {
    setCheckingOut(planId);
    try {
      const front = window.location.origin;
      const r = await api.post("/billing/checkout", {
        plan_id: planId,
        success_url: `${front}/app/plans?success=1&session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: `${front}/app/plans?cancel=1`,
      });
      if (r?.data?.url) {
        window.location.href = r.data.url;
      } else if (r?.data?.swapped) {
        toast.success(r.data.message || "Plan swapped.");
        refreshBilling();
      } else {
        toast.error("Couldn't create checkout session. Try again?");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || "Checkout failed. Try again?";
      toast.error(typeof detail === "string" ? detail : "Checkout failed.");
    } finally { setCheckingOut(null); }
  };

  const openPortal = async () => {
    setLoadingPortal(true);
    try {
      const r = await api.post("/billing/portal");
      if (r?.data?.url) window.location.href = r.data.url;
    } catch (err) {
      const detail = err?.response?.data?.detail || "Couldn't open the portal.";
      toast.error(typeof detail === "string" ? detail : "Portal failed.");
    } finally { setLoadingPortal(false); }
  };

  const downgradeToFree = async () => {
    if (!window.confirm("Cancel your paid plan at the end of the current billing period? You'll keep your current plan until then.")) return;
    setDowngrading(true);
    try {
      const r = await api.post("/billing/select-free");
      if (r?.data?.scheduled_cancel) {
        toast.success("Cancellation scheduled — you keep access until your period ends.");
      } else {
        toast.success("You're on the Free plan.");
      }
      refreshBilling();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Couldn't schedule cancellation.";
      toast.error(typeof detail === "string" ? detail : "Cancellation failed.");
    } finally { setDowngrading(false); }
  };

  const buyPack = async (packId) => {
    setBuyingPack(packId);
    try {
      const front = window.location.origin;
      const r = await api.post("/billing/credit-packs/checkout", {
        pack_id: packId,
        success_url: `${front}/app/plans?success=1&pack=1&session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: `${front}/app/plans?cancel=1`,
      });
      if (r?.data?.url) {
        window.location.href = r.data.url;
      } else {
        toast.error("Couldn't create checkout session. Try again?");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || "Checkout failed. Try again?";
      toast.error(typeof detail === "string" ? detail : "Checkout failed.");
    } finally { setBuyingPack(null); }
  };

  const syncBilling = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/billing/sync-checkout", {});
      if (r?.data?.billing) {
        setBilling(r.data.billing);
        window.dispatchEvent(new Event("pm-billing-refresh"));
      }
      const applied = r?.data?.applied || {};
      if (applied.subscription) {
        toast.success(`Subscription reconnected — you're on ${r.data.billing.plan_name}.`);
      } else if (applied.credit_pack) {
        toast.success("Credit pack applied.");
      } else if (applied.found_customer) {
        toast.info("Your billing is already up to date.");
      } else {
        toast.info("No pending Stripe activity found for this account.");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || "Couldn't reach Stripe. Try again in a moment.";
      toast.error(typeof detail === "string" ? detail : "Sync failed.");
    } finally { setSyncing(false); }
  };

  const status = billing?.subscription_status;
  const statusInfo = status ? STATUS_COPY[status] : null;

  return (
    <div className="max-w-5xl mx-auto" data-testid="plans-page">
      <div className="text-center mb-8">
        <h1 className="font-display text-4xl md:text-5xl font-bold mb-3">Choose your plan</h1>
        <p className="text-lg" style={{ color: "var(--text-secondary)" }}>
          Start free with 6 one-time credits, or unlock the full app with a monthly refill.
        </p>
        <div
          className="mt-4 inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs"
          style={{ background: "var(--paper)", border: "1.5px solid var(--border)", color: "var(--text-secondary)" }}
          data-testid="plans-credit-rule"
        >
          <Info size={12} style={{ color: "var(--primary)" }} />
          <span><b>{creditRule}</b></span>
        </div>
      </div>

      {/* Current-plan banner */}
      {billing && (
        <div
          className="pm-card p-4 mb-6 flex flex-wrap items-center justify-between gap-3"
          data-testid="plans-current-status"
          style={{ border: `1.5px solid ${PLAN_ACCENTS[billing.plan]}` }}
        >
          <div>
            <div className="text-xs uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>
              Current plan
            </div>
            <div className="font-display font-bold text-xl flex items-center gap-2 flex-wrap" style={{ color: PLAN_ACCENTS[billing.plan] }}>
              {billing.plan_name}
              {statusInfo && (
                <span
                  className="pm-badge text-xs"
                  data-testid="plans-status-badge"
                  style={{ background: `${statusInfo.tone}22`, color: statusInfo.tone }}
                >
                  {statusInfo.label}
                </span>
              )}
              {billing.cancel_at_period_end && billing.current_period_end && (
                <span className="text-sm font-normal" style={{ color: "var(--text-muted)" }} data-testid="plans-cancel-scheduled">
                  · ends {new Date(billing.current_period_end).toLocaleDateString()}
                </span>
              )}
              {!billing.cancel_at_period_end && billing.plan !== "free" && billing.current_period_end && (
                <span className="text-sm font-normal" style={{ color: "var(--text-muted)" }}>
                  · renews {new Date(billing.current_period_end).toLocaleDateString()}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-4 text-sm flex-wrap" style={{ color: "var(--text-secondary)" }}>
            <span data-testid="plans-credits-remaining">
              <b style={{ color: PLAN_ACCENTS[billing.plan], fontSize: "1.15em" }}>
                {billing.credits_remaining}
              </b>
              /{billing.credits_total} Open Topic Credits
              {billing.credits_one_time && (
                <span className="ml-1" style={{ color: "var(--text-muted)" }}>
                  · one-time
                </span>
              )}
            </span>
            <button
              onClick={syncBilling}
              disabled={syncing}
              className="text-xs inline-flex items-center gap-1.5 hover:underline"
              style={{ color: "var(--text-muted)" }}
              title="Recently paid but plan hasn't updated? Click to reconnect with Stripe."
              data-testid="plans-sync-btn"
            >
              {syncing ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
              Sync with Stripe
            </button>
            {billing.plan !== "free" && (
              <>
                <button
                  onClick={openPortal}
                  disabled={loadingPortal}
                  className="text-xs inline-flex items-center gap-1.5 hover:underline"
                  style={{ color: "var(--text-muted)" }}
                  data-testid="plans-portal-btn"
                >
                  {loadingPortal ? <Loader2 size={12} className="animate-spin" /> : <ExternalLink size={12} />}
                  Manage billing
                </button>
                {!billing.cancel_at_period_end && (
                  <button
                    onClick={downgradeToFree}
                    disabled={downgrading}
                    className="text-xs hover:underline"
                    style={{ color: "var(--text-muted)" }}
                    data-testid="plans-cancel-btn"
                  >
                    Cancel subscription
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Plan cards */}
      <div className="grid md:grid-cols-3 gap-4" data-testid="plans-grid">
        {plans.map((p) => {
          const Icon = PLAN_ICONS[p.id] || Sparkles;
          const isCurrent = billing?.plan === p.id;
          const accent = PLAN_ACCENTS[p.id];
          return (
            <div
              key={p.id}
              data-testid={`plan-card-${p.id}`}
              className="pm-card p-6 flex flex-col"
              style={{
                border: `1.5px solid ${isCurrent ? accent : "var(--border)"}`,
                boxShadow: isCurrent ? `0 0 0 3px ${accent}22` : undefined,
                background: p.id === "pro" ? "linear-gradient(180deg, rgba(201,138,59,0.05), transparent)" : undefined,
              }}
            >
              <div className="flex items-center gap-2 mb-2">
                <div
                  className="w-9 h-9 rounded-xl flex items-center justify-center"
                  style={{ background: `${accent}22`, color: accent }}
                >
                  <Icon size={18} />
                </div>
                <div className="font-display font-bold text-2xl uppercase tracking-wide">{p.name}</div>
              </div>

              <div className="flex items-baseline gap-1 mb-3">
                <span className="font-display font-bold text-4xl">
                  ${(p.price_cents / 100).toFixed(2)}
                </span>
                <span className="text-sm" style={{ color: "var(--text-muted)" }}>
                  /{p.credits_one_time ? "one-time" : p.billing_interval}
                </span>
              </div>

              <div
                className="pm-badge mb-4 self-start"
                style={{ background: `${accent}22`, color: accent }}
                data-testid={`plan-credits-${p.id}`}
              >
                {p.credits_per_period} Open Topic Credits{p.credits_one_time ? "" : "/month"}
              </div>

              {p.welcome_bonus_credits > 0 && billing && !billing.welcome_bonus_granted && (
                <div
                  className="mb-4 p-3 rounded-xl flex items-start gap-2.5"
                  style={{
                    background: `${accent}14`,
                    border: `1.5px dashed ${accent}`,
                  }}
                  data-testid={`plan-welcome-bonus-${p.id}`}
                >
                  <Gift size={18} style={{ color: accent, flexShrink: 0, marginTop: 2 }} />
                  <div className="flex-1">
                    <div className="font-display font-bold text-sm" style={{ color: accent }}>
                      First-subscription bonus
                    </div>
                    <div className="text-xs mt-0.5" style={{ color: "var(--text-secondary)" }}>
                      Get <strong>+{p.welcome_bonus_credits} bonus credits</strong> that never expire — added to your account the moment you subscribe. One-time only.
                    </div>
                  </div>
                </div>
              )}

              <ul className="space-y-2 mb-6 flex-1" data-testid={`plan-features-${p.id}`}>
                {p.features.map((f, i) => (
                  <li key={i} className="text-sm flex items-start gap-2">
                    <Check size={14} className="mt-0.5 flex-shrink-0" style={{ color: accent }} />
                    <span style={{ color: "var(--text-secondary)" }}>{f}</span>
                  </li>
                ))}
              </ul>

              {billing === null ? (
                <button
                  disabled
                  className="pm-btn-secondary w-full opacity-60 cursor-default inline-flex items-center justify-center gap-2"
                  data-testid={`plan-cta-${p.id}`}
                >
                  <Loader2 size={14} className="animate-spin" /> Loading…
                </button>
              ) : isCurrent ? (
                <button
                  disabled
                  className="pm-btn-secondary w-full opacity-60 cursor-default"
                  data-testid={`plan-cta-${p.id}`}
                >
                  Your current plan
                </button>
              ) : !p.requires_checkout ? (
                <button
                  onClick={downgradeToFree}
                  disabled={downgrading}
                  className="pm-btn-secondary w-full inline-flex items-center justify-center gap-2"
                  data-testid={`plan-cta-${p.id}`}
                >
                  {downgrading ? <><Loader2 size={14} className="animate-spin" /> Scheduling…</> : "Switch to Free"}
                </button>
              ) : (
                <button
                  onClick={() => checkout(p.id)}
                  disabled={checkingOut === p.id}
                  data-testid={`plan-cta-${p.id}`}
                  className="pm-btn-primary w-full inline-flex items-center justify-center gap-2"
                  style={{ background: accent, borderColor: accent }}
                >
                  {checkingOut === p.id ? (
                    <><Loader2 size={14} className="animate-spin" /> Opening checkout…</>
                  ) : billing?.plan === "free" ? (
                    <><CreditCard size={14} /> Upgrade to {p.name}</>
                  ) : billing?.plan && billing.plan !== p.id ? (
                    <><CreditCard size={14} /> Switch to {p.name}</>
                  ) : (
                    <><CreditCard size={14} /> Get {p.name}</>
                  )}
                </button>
              )}
            </div>
          );
        })}
      </div>

      <p className="text-xs text-center mt-8" style={{ color: "var(--text-muted)" }}>
        Payments powered by Stripe. Cancel anytime — you keep your paid plan until the end of the billing period. Plus &amp; Pro credits refresh every billing anniversary and don&apos;t roll over. Need extra credits mid-month? Grab a top-up pack below.
      </p>

      {/* One-time credit top-up packs */}
      {creditPacks.length > 0 && (
        <div id="credit-packs" className="mt-12 pt-8 border-t" style={{ borderColor: "var(--border)" }} data-testid="credit-packs-section">
          <div className="text-center mb-6">
            <h2 className="font-display text-3xl md:text-4xl font-bold mb-2">Need more credits?</h2>
            <p className="text-base" style={{ color: "var(--text-secondary)" }}>
              Top up any time — added instantly to your current billing period.
            </p>
            {creditPackNote && (
              <div
                className="mt-3 inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs"
                style={{ background: "var(--paper)", border: "1.5px solid var(--border)", color: "var(--text-muted)" }}
                data-testid="credit-packs-note"
              >
                <Info size={12} style={{ color: "var(--primary)" }} />
                <span>{creditPackNote}</span>
              </div>
            )}
          </div>

          <div className="grid md:grid-cols-3 gap-4" data-testid="credit-packs-grid">
            {creditPacks.map((p) => {
              const accent = PACK_ACCENTS[p.id] || "#4f7942";
              const isBest = p.id === "credits_20";
              const perCredit = (p.price_cents / p.credits / 100).toFixed(2);
              return (
                <div
                  key={p.id}
                  data-testid={`credit-pack-card-${p.id}`}
                  className="pm-card p-5 flex flex-col relative"
                  style={{
                    border: `1.5px solid ${isBest ? accent : "var(--border)"}`,
                    boxShadow: isBest ? `0 0 0 3px ${accent}22` : undefined,
                  }}
                >
                  {isBest && (
                    <div
                      className="pm-badge absolute -top-3 right-4 text-xs font-bold uppercase tracking-wider"
                      style={{ background: accent, color: "#fff" }}
                      data-testid={`credit-pack-badge-${p.id}`}
                    >
                      Best value
                    </div>
                  )}
                  <div className="flex items-center gap-2 mb-2">
                    <div
                      className="w-9 h-9 rounded-xl flex items-center justify-center"
                      style={{ background: `${accent}22`, color: accent }}
                    >
                      <Plus size={18} />
                    </div>
                    <div className="font-display font-bold text-xl">{p.credits} Credits</div>
                  </div>
                  <div className="flex items-baseline gap-1 mb-1">
                    <span className="font-display font-bold text-3xl">
                      ${(p.price_cents / 100).toFixed(2)}
                    </span>
                    <span className="text-sm" style={{ color: "var(--text-muted)" }}>one-time</span>
                  </div>
                  <div className="text-xs mb-4" style={{ color: "var(--text-muted)" }} data-testid={`credit-pack-percredit-${p.id}`}>
                    ${perCredit} per credit
                  </div>
                  <button
                    onClick={() => buyPack(p.id)}
                    disabled={buyingPack === p.id}
                    data-testid={`credit-pack-cta-${p.id}`}
                    className="pm-btn-primary w-full inline-flex items-center justify-center gap-2 mt-auto"
                    style={{ background: accent, borderColor: accent }}
                  >
                    {buyingPack === p.id ? (
                      <><Loader2 size={14} className="animate-spin" /> Opening checkout…</>
                    ) : (
                      <><CreditCard size={14} /> Buy {p.credits} credits</>
                    )}
                  </button>
                </div>
              );
            })}
          </div>
          <p className="text-xs text-center mt-6" style={{ color: "var(--text-muted)" }}>
            One-time top-up credits do not expire and remain available until used. Stack any number of packs — they always add to your balance.
          </p>
        </div>
      )}
    </div>
  );
}
